/* eslint-disable no-unused-vars */
/* eslint-disable no-new */

const CONTEXT_FILTER = {
  NEW_CONTEXT_ONLY: 'NEW_CONTEXT_ONLY',
  INCLUDE_EXISTING_CHALLENGES: 'INCLUDE_EXISTING_CHALLENGES',
  NEW_PLAYERS_ONLY: 'NEW_PLAYERS_ONLY',
};

const SHARE_INTENT = {
  INVITE: 'INVITE',
  REQUEST: 'REQUEST',
  CHALLENGE: 'CHALLENGE',
  SHARE: 'SHARE',
};

export const SOCIAL_STATUS = { // Not actually your social status!
  PASS: 'PASS',
  FAIL: 'FAIL',
  USER_INPUT: 'USER_INPUT',
  SAME_CONTEXT: 'SAME_CONTEXT',
};

/**
 * class for social interactions
 */
export class OMT_Social {
  /**
   * constructor
   */
  constructor() {
    this._didSendMsg = false;
  }

  /**
   * true if the user has sent a messag
   * @returns {boolean}
   */
  get didSendMsg() { return this._didSendMsg; }

  /**
   * get the current context type
   * @returns {string}
   */
  getCurrentContextType() {
    return FBInstant.context.getType();
  }

  /**
   * track admin message sent
   * @param {string} messageName
   */
  sendMessageSentToFBA(updateOptions) {
    const messageName = updateOptions.data.sendParams ? updateOptions.data.sendParams.msgName : updateOptions.data.inGameMsg;
    OMT.platformTracking.logEvent(OMT.platformTracking.Events.AdminMessageSent, 1, { messageName });
  }

  /**
   * track admin message click
   * @param {string} messageName
   */
  sendMessageClickToFBA(messageName) {
    OMT.platformTracking.logEvent(OMT.platformTracking.Events.AdminMessageClick, 1, { messageName });
  }

  /**
   * Opens FB dialog to pick a friend to switch the context to
   * and updates that player with a message
   * @param {Object} updateOptions
   * @param {Object} filterArray
   * @returns {boolean}
   */
  async chooseContextAndUpdate(updateOptions, filterArray) {
    this.showOverlay();
    try {
      await FBInstant.context.chooseAsync({ filters: filterArray });
      await FBInstant.updateAsync(updateOptions);
      this._didSendMsg = true;
      this.hideOverlay();
      this.sendMessageSentToFBA(updateOptions);
      return true;
    } catch (e) {
      this.hideOverlay();
      if (e.code === 'USER_INPUT') {
        // console.log('chooseContextAndUpdate() user aborted');
      } else if (e.code === 'SAME_CONTEXT') {
        // console.log('chooseContextAndUpdate() user is already in the same context');
        return true;
      } else {
        console.error(e);
      }
      return false;
    }
  }

  /**
   * Creates a context with given user and updates with a message
   * @param {string} userId
   * @param {Object} updateOptions
   * @returns {boolean}
   */
  async createContextAndUpdate(userId, updateOptions) {
    this.showOverlay();
    try {
      const inUserThread = await this.checkIfInUserThreadContext(userId);
      if (!inUserThread) await FBInstant.context.createAsync(userId);
      await FBInstant.updateAsync(updateOptions);
      this._didSendMsg = true;
      this.sendMessageSentToFBA(updateOptions);
      this.hideOverlay();
      return true;
    } catch (e) {
      this.hideOverlay();
      if (e.code === 'USER_INPUT') {
        // console.log('createContextAndUpdate() user aborted');
        return false;
      }
      if (e.code === 'SAME_CONTEXT') {
        // console.log('createContextAndUpdate() user is already in the same context');
        return true;
      }
      console.error(e);
      return false;
    }
  }

  /**
   * check if the user is already in a context with this user
   * @param {string} userId
   * @returns {boolean}
   */
  async checkIfInUserThreadContext(userId) {
    if (FBInstant.context.getType() !== 'THREAD') return false;
    const playersList = await FBInstant.context.getPlayersAsync();
    let playerObj;
    for (let i = 0; i < playersList.length; i++) {
      // eslint-disable-next-line dot-notation
      playerObj = playersList[i]['$1'];
      if (playerObj.id === userId) return true;
    }
    return false;
  }

  /**
   * get a list of players in the current context
   * @returns {Array}
   */
  async getPlayersInContext() {
    const players = await FBInstant.context.getPlayersAsync();
    return players;
  }

  /**
   * Opens FB dialog to pick a friend to switch the context to
   * but goes through an async function to check if its ok to proceed
   * before updating.
   * Returns status instead of boolean
   * @param {Object} updateOptions
   * @param {Function} check
   * @param {Object} filterArray
   * @returns {string}
   */
  async createCheckUpdateContext(updateOptions, check, filterArray) {
    this.showOverlay();

    try {
      await FBInstant.context.chooseAsync({ filters: filterArray });
      const okToProceed = await check();
      if (okToProceed) {
        await FBInstant.updateAsync(updateOptions);
        this.sendMessageSentToFBA(updateOptions);
        this._didSendMsg = true;
      }
      this.hideOverlay();
      return okToProceed ? SOCIAL_STATUS.PASS : SOCIAL_STATUS.FAIL;
    } catch (e) {
      this.hideOverlay();
      switch (e.code) {
        case SOCIAL_STATUS.USER_INPUT: return SOCIAL_STATUS.USER_INPUT;
        case SOCIAL_STATUS.SAME_CONTEXT: return SOCIAL_STATUS.SAME_CONTEXT;
        default: return e.code;
      }
    }
  }

  /**
   * choose context and invite user
   * @returns {Promise}
   */
  async chooseContextAndInvite() {
    const inGameMsg = 'Invite';
    this.sendMessageClickToFBA(inGameMsg);
    return new Promise((resolve, reject) => {
      new G.MsgInvite(FBInstant.player.getPhoto(), async (msgObj) => {
        const img = OMT.utils.msgToBase64(msgObj);
        msgObj.destroy();
        const result = await this.chooseContextAndUpdate({
          action: 'CUSTOM',
          image: img,
          text: { // set proper text
            default: OMT.language.getText("Hey! Let's play %GameName% together!"),
            localizations: OMT.language.generateFBLocalization("Hey! Let's play %GameName% together!", '%GameName%', G.OMTsettings.global.gameName),
          },
          template: 'INVITE', // what should be here????? 🤔
          strategy: 'IMMEDIATE_CLEAR',
          data: {
            inGameMsg,
            sendParams: this._createMsgSendParams(inGameMsg, 'SHARE'),
          },
        }, [CONTEXT_FILTER.NEW_PLAYERS_ONLY]);
        resolve(result);
      });
    });
  }

  /**
   * send global high score beat message to another user
   * @param {Object} currentUser
   * @param {Object} otherUser
   * @param {Function} callback
   */
  sendHighscoreBeatMsg(currentUser, otherUser, callback) {
    const inGameMsg = 'BeatHighScore';
    this.sendMessageClickToFBA(inGameMsg);
    const txt = game.rnd.pick([
      'Did you think that you could be in first place forever? Regain your title',
      '%NAME% has beaten your high score. Rise to the challenge',
      'Did you see that %NAME% has just beaten your high score?',
    ]);
    new G.MsgHighscoreBeaten(currentUser, otherUser, (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      const updateOptions = {
        action: 'CUSTOM',
        image: img,
        text: {
          default: txt.replace('%NAME%', currentUser.name),
          localizations: OMT.language.generateFBLocalization(txt, '%NAME%', currentUser.name),
        },
        template: 'INVITE',
        strategy: 'IMMEDIATE_CLEAR',
        data: {
          inGameMsg,
          sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
        },
      };

      this.createContextAndUpdate(otherUser.id, updateOptions)
        .then((success) => {
          if (success) {
            DDNA.socialTracker.incrementParam('numFriendsChallengeSent', 1);
            DDNA.socialTracker.markFriendInteraction(otherUser.id);
            DDNA.tracking.socialActionEvent('BeatHighScore', '', otherUser.userId, '');
          }
          if (callback) callback(success);
        });
    });
  }


  /**
   * send level high score beat message to another user
   * @param {Object} currentUser
   * @param {Object} otherUser
   * @param {Function} callback
   */
  sendLevelHighscoreBeatMsg(currentUser, otherUser, lvlIndex, scoreDifference, callback) {
    const inGameMsg = 'BeatLvlHighScore';
    this.sendMessageClickToFBA(inGameMsg);
    const txt = game.rnd.pick([
      '%NAME% scored %POINTS DIFFERENCE% more than you!',
      'Your score has been beaten by %POINTS DIFFERENCE%',
      'Surprise! There is %POINTS DIFFERENCE% between you and the new high score.',
    ]);
    new G.MsgBeaten(currentUser, otherUser, (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      const updateOptions = {
        action: 'CUSTOM',
        image: img,
        text: {
          default: txt.replace('%NAME%', currentUser.name).replace('%POINTS DIFFERENCE%', scoreDifference.toString()),
          localizations: OMT.language.generateFBLocalization(txt, ['%NAME%', '%POINTS DIFFERENCE%'], [currentUser.name, scoreDifference.toString()]),
        },
        template: 'INVITE',
        strategy: 'IMMEDIATE_CLEAR',
        data: {
          inGameMsg,
          sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
          command: { goToLevel: { levelIndex: lvlIndex } },
        },
      };

      this.createContextAndUpdate(otherUser.id, updateOptions)
        .then((success) => {
          if (success) {
            DDNA.socialTracker.incrementParam('numFriendsChallengeSent', 1);
            DDNA.socialTracker.markFriendInteraction(otherUser.id);
            DDNA.tracking.socialActionEvent('BeatLvlHighScore', '', otherUser.id, '');
          }
          if (callback) callback(success);
        });
    });
  }

  /**
   * send a gate response message to a user.
   * @param {string} friendId
   * @param {string} gateId
   */
  sendGateMsg(friendId, gateId) {
    const inGameMsg = 'GateMsg';
    this.sendMessageClickToFBA(inGameMsg);
    return new Promise((resolve, reject) => {
      const txt = "Hey buddy! Here's the gate key :)";
      new G.MsgGate(async (msgObj) => {
        const img = OMT.utils.msgToBase64(msgObj);
        msgObj.destroy();
        const updateOptions = {
          action: 'CUSTOM',
          image: img,
          text: { default: txt, localizations: OMT.language.generateFBLocalization(txt) },
          template: 'INVITE',
          strategy: 'IMMEDIATE_CLEAR',
          data: {
            inGameMsg,
            sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
            gate: {
              sender: OMT.envData.settings.user.userId,
              forPlayer: friendId,
              senderAvatar: OMT.envData.settings.user.avatar,
              gateId,
              timestamp: Date.now(),
            },
          },
        };
        const result = await OMT.social.createContextAndUpdate(friendId, updateOptions);
        if (result) {
          DDNA.socialTracker.markFriendInteraction(friendId);
          DDNA.tracking.socialActionEvent('GateMsg', '', friendId, '');
        }
        resolve(result);
      });
    });
  }

  /**
   * send a gate open request message
   * @param {string} friendId
   * @param {string} gateId
   * @param {Function} callback
   */
  requestGateMsg(friendId, gateId, callback) {
    const inGameMsg = 'GateReqMsg';
    this.sendMessageClickToFBA(inGameMsg);
    return new Promise((resolve, reject) => {
      const txt = 'Hey, please help me in opening the gate! :)';
      new G.MsgGateRequest(async (msgObj) => {
        const img = OMT.utils.msgToBase64(msgObj);
        msgObj.destroy();
        const updateOptions = {
          action: 'CUSTOM',
          image: img,
          text: { default: txt, localizations: OMT.language.generateFBLocalization(txt) },
          template: 'INVITE',
          strategy: 'IMMEDIATE_CLEAR',
          data: {
            inGameMsg,
            sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
            gateRequest: {
              sender: OMT.envData.settings.user.userId,
              senderAvatar: OMT.envData.settings.user.avatar,
              gbUserId: OMT.envData.gbUserId,
              gateId,
              timestamp: Date.now(),
            },
          },
        };
        const result = await OMT.social.createContextAndUpdate(friendId, updateOptions);
        if (result) {
          DDNA.socialTracker.markFriendInteraction(friendId);
          DDNA.tracking.socialActionEvent('GateReqMsg', '', friendId, '');
        }
        resolve(result);
      });
    });
  }

  /**
   * send a message to a friend giving them coins
   * @param {string} friendId
   * @param {Function} callback
   */
  sendCoinsMessage(friendId, callback) {
    const inGameMsg = 'SEND_COIN';
    this.sendMessageClickToFBA(inGameMsg);
    const txt = 'Thanks for the free coins! Get yours too!';
    new G.MsgCoins(OMT.envData.settings.user.avatar, (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      this.createContextAndUpdate(friendId, {
        action: 'CUSTOM',
        image: img,
        text: { default: txt, localizations: OMT.language.generateFBLocalization(txt) },
        template: 'INVITE',
        strategy: 'IMMEDIATE_CLEAR',
        data: {
          inGameMsg,
          sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
        },
      })
        .then((success) => {
          if (callback) callback(success);
        });
    });
  }

  /**
   * send a message when a unfufilled request help scenario occurrs
   * @param {Function} callback
   */
  sendClosedHelpRequest(callback) {
    if (this._didSendMsg === true || FBInstant.getEntryPointData() === null) return;
    const inGameMsg = 'closedHelpRequest';
    this.sendMessageClickToFBA(inGameMsg);
    const txt = "Let's help each other, I want to play %GameName% with you!";
    const ctatxt = 'Claim your reward!';
    new G.MsgHelpClosed(OMT.envData.settings.user.avatar, (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      FBInstant.updateAsync({
        action: 'CUSTOM',
        cta: { default: ctatxt, localizations: OMT.language.generateFBLocalization(ctatxt) },
        image: img,
        text: {
          default: txt.replace('%GameName%', G.OMTsettings.global.gameName),
          localizations: OMT.language.generateFBLocalization(txt, '%GameName%', G.OMTsettings.global.gameName),
        },
        template: 'INVITE',
        strategy: 'LAST',
        data: {
          inGameMsg,
          sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
        },
      }).then((success) => {
        DDNA.tracking.socialActionEvent('closedHelpRequest', '', '', '');
        if (callback) callback(success);
      });
    });
  }

  /**
   * send a message to a friend giving them a life
   * @param {string} friendId
   * @param {Function} callback
   */
  sendLifeMessage(friendId, callback) {
    const inGameMsg = 'SEND_LIFE';
    this.sendMessageClickToFBA(inGameMsg);
    new G.MsgLives((msgObj) => {
      if (!G.saveState.canResourceBeSent(friendId, 'LifeSentToUser')) { msgObj.destroy(); return; }
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      this.createContextAndUpdate(friendId, {
        action: 'CUSTOM',
        image: img,
        text: {
          default: ('(name) just sent you a life!').replace('(name)', OMT.envData.settings.user.name),
          localizations: OMT.language.generateFBLocalization('(name) just sent you a life!', '(name)', OMT.envData.settings.user.name),
        },
        template: 'INVITE',
        strategy: 'IMMEDIATE_CLEAR',
        data: {
          inGameMsg,
          sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
          life: {
            sender: OMT.envData.settings.user.userId,
            timestamp: Date.now(),
          },
        },
      })
        .then((success) => {
          if (success) {
            DDNA.socialTracker.incrementParam('numSendLives', 1);
            DDNA.socialTracker.markFriendInteraction(friendId);
            DDNA.tracking.socialActionEvent('SEND_LIFE', '', friendId, '');
            OMT.platformTracking.logEvent(OMT.platformTracking.Events.LifeSentFromUser);
            // dispatch the event to complete the send life daily mission
            G.sb('lifeMsgSent').dispatch();
          }
          callback(success);
        });
    });
  }

  /**
   * send a request life message to a friend
   * @param {string} friendId
   * @param {Function} callback
   */
  sendRequestLifeMessage(friendId, callback) {
    const inGameMsg = 'REQUEST_LIFE';
    this.sendMessageClickToFBA(inGameMsg);
    new G.MsgLivesRequest(OMT.envData.settings.user.avatar, (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      this.createContextAndUpdate(friendId, {
        action: 'CUSTOM',
        image: img,
        text: {
          default: ('(name) needs you! Send them a life!').replace('(name)', OMT.envData.settings.user.name),
          localizations: OMT.language.generateFBLocalization('(name) needs you! Send them a life!', '(name)', OMT.envData.settings.user.name),
        },
        template: 'INVITE',
        strategy: 'IMMEDIATE_CLEAR',
        data: {
          inGameMsg,
          sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
          lifeRequest: {
            sender: OMT.envData.settings.user.userId,
            timestamp: Date.now(),
            context: FBInstant.context.getID(), // Todo: is this realy being used?
          },
        },
      })
        .then((success) => {
          if (success) {
            DDNA.socialTracker.incrementParam('numLivesRequested', 1);
            DDNA.socialTracker.markFriendInteraction(friendId);
            DDNA.tracking.socialActionEvent('REQUEST_LIFE', '', friendId, '');
            OMT.platformTracking.logEvent(OMT.platformTracking.Events.LifeRequestedFromUser);
          }
          callback(success);
        });
    });
  }

  /**
   * send map overtake message to a friend
   * @param {Object} currentUser
   * @param {Object} otherUser
   * @param {Function} callback
   */
  sendOvertakenMsg(currentUser, otherUser, callback) {
    const inGameMsg = 'OvertakenMsg';
    this.sendMessageClickToFBA(inGameMsg);
    const txt = game.rnd.pick([
      '%NAME% is now level %LVLNR%!',
      'Hurry, %NAME% is now level %LVLNR%.',
      '%NAME% is progressing quickly on the map.',
    ]);
    const levelNum = G.saveState.getLastPassedLevelNr() + 1;
    new G.MsgOvertaken(currentUser, otherUser, (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      const updateOptions = {
        action: 'CUSTOM',
        image: img,
        text: {
          default: txt.replace('%NAME%', currentUser.name).replace('%LVLNR%', levelNum),
          localizations: OMT.language.generateFBLocalization(txt, ['%NAME%', '%LVLNR%'], [currentUser.name, levelNum]),
        },
        template: 'INVITE',
        strategy: 'IMMEDIATE_CLEAR',
        data: {
          inGameMsg,
          sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
        },
      };
      this.createContextAndUpdate(otherUser.id, updateOptions)
        .then((success) => {
          if (success) {
            DDNA.socialTracker.incrementParam('numFriendsChallengeSent', 1);
            DDNA.socialTracker.markFriendInteraction(otherUser.id);
            DDNA.tracking.socialActionEvent('OvertakenMsg', '', otherUser.userId, '');
          }
          if (callback) callback(success);
        });
    });
  }

  /**
   * Send a Request Help message to a friend
   * @param {Object} currentUser
   * @returns {Promise}
   */
  async sendRequestHelpMessage(otherUser, fromHelper = false) {
    const inGameMsg = 'requestHelpMsg';
    this.sendMessageClickToFBA(inGameMsg);
    return new Promise((resolve, reject) => {
      new G.MsgRequestHelp(FBInstant.player.getPhoto(), async (msgObj) => {
        const img = OMT.utils.msgToBase64(msgObj);
        msgObj.destroy();
        const updateOptions = {
          action: 'CUSTOM',
          image: img,
          text: { // set proper text
            default: 'Spin the Booster Wheel to claim your reward!',
            localizations: OMT.language.generateFBLocalization('Spin the Booster Wheel to claim your reward!'),
          },
          data: {
            sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
            inGameMsg,
            requestHelp: {
              otherUserId: otherUser.id,
              fromHelper,
            },
          },
          template: 'INVITE', // what should be /here/!????? 🤔
          strategy: 'IMMEDIATE_CLEAR',
        };

        const result = await OMT.social.createContextAndUpdate(otherUser.id, updateOptions);
        if (result) {
          DDNA.socialTracker.markFriendInteraction(otherUser.id);
          DDNA.tracking.socialActionEvent('requestHelpMsg', '', otherUser.userId, '');
        }
        resolve(result);
      });
    });
  }

  /**
   * Send a Ads fallback spin message to a friend
   * @param {Object} currentUser
   * @returns {Promise}
   */
  async sendAdsFallbackMessage(otherUser) {
    const inGameMsg = 'adsFallbackMsg';
    this.sendMessageClickToFBA(inGameMsg);
    return new Promise((resolve, reject) => {
      new G.MsgAdsFallback(FBInstant.player.getPhoto(), async (msgObj) => {
        const img = OMT.utils.msgToBase64(msgObj);
        msgObj.destroy();
        const updateOptions = {
          action: 'CUSTOM',
          image: img,
          text: { // set proper text
            default: 'Thank you for helping me.',
            localizations: OMT.language.generateFBLocalization('Thank you for helping me.'),
          },
          data: {
            sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
          },
          template: 'INVITE', // what should be /here/!????? 🤔
          strategy: 'IMMEDIATE_CLEAR',
        };

        const result = await OMT.social.createContextAndUpdate(otherUser.id, updateOptions);
        if (result) {
          DDNA.socialTracker.markFriendInteraction(otherUser.id);
          DDNA.tracking.socialActionEvent('adsFallbackMsg', '', otherUser.userId, '');
        }
        resolve(result);
      });
    });
  }


  /**
   * Send a friendship chest invite message
   */
  sendFriendshipChestInvite(checkFunction) {
    const inGameMsg = 'friendshipChestInvite';
    this.sendMessageClickToFBA(inGameMsg);
    return new Promise((resolve, reject) => {
      new G.MsgFriendshipChestInvite(FBInstant.player.getPhoto(), async (msgObj) => {
        const img = OMT.utils.msgToBase64(msgObj);
        msgObj.destroy();

        const result = await this.createCheckUpdateContext({
          action: 'CUSTOM',
          image: img,
          text: { // set proper text
            default: 'Start playing so you and your friend earn a reward!',
            localizations: OMT.language.generateFBLocalization('Start playing so you and your friend earn a reward!'),
          },
          template: 'INVITE', // what should be here????? 🤔
          strategy: 'IMMEDIATE',
          data: {
            sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
            inGameMsg,
            friendshipChestInvite: {
              inviterFbId: OMT.envData.settings.user.userId,
              inviterGbId: `${OMT.envData.gbUserId}`, // Has to be GB id
            },
          },
        }, checkFunction, [CONTEXT_FILTER.NEW_PLAYERS_ONLY]);
        resolve(result);
      });
    });
  }

  /**
   * Send a Friendship chest I have joined message
   * @param {Object} currentUser
   */
  sendFriendshipChestJoined(originalSenderId) {
    const inGameMsg = 'friendshipChestJoin';
    this.sendMessageClickToFBA(inGameMsg);
    return new Promise((resolve, reject) => {
      const playerName = FBInstant.player.getName();
      new G.MsgFriendshipChestJoined(FBInstant.player.getPhoto(), async (msgObj) => {
        const img = OMT.utils.msgToBase64(msgObj);
        msgObj.destroy();
        const updateOptions = {
          action: 'CUSTOM',
          image: img,
          text: { // set proper text)
            default: 'Your friend %Name% joined the game!'.replace('%Name%', playerName),
            localizations: OMT.language.generateFBLocalization('Your friend %Name% joined the game!', '%Name%', playerName),
          },
          data: {
            sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
            inGameMsg,
            friendshipChestJoin: {},
          },
          template: 'INVITE', // what should be /here/!????? 🤔
          strategy: 'IMMEDIATE',
        };

        const result = await OMT.social.createContextAndUpdate(originalSenderId, updateOptions);
        if (result) {
          DDNA.socialTracker.markFriendInteraction(originalSenderId);
          DDNA.tracking.socialActionEvent('friendshipChestJoin', '', originalSenderId, '');
        }
        resolve(result);
      });
    });
  }

  /**
   * send an event postcard
   * @param {number} cardIndex index of the card to display
   */
  sendEventPostCard(cardIndex) {
    const inGameMsg = 'POSTCARD_MSG';
    this.sendMessageClickToFBA(inGameMsg);
    return new Promise((resolve, reject) => {
      new G.MsgEventPostcard(cardIndex, async (msgObj) => {
        const img = OMT.utils.msgToBase64(msgObj);
        msgObj.destroy();

        const result = await this.chooseContextAndUpdate({
          action: 'CUSTOM',
          image: img,
          text: { // set proper text
            default: 'You have a new Secret Admirer',
            localizations: OMT.language.generateFBLocalization('You have a new Secret Admirer'),
          },
          template: 'INVITE', // what should be here????? 🤔
          strategy: 'IMMEDIATE',
          data: {
            sendParams: this._createMsgSendParams(inGameMsg, 'ADMIN'),
            inGameMsg,
            postcardEvent: {
              eventId: G.OMTsettings.postcardEvent.eventId,
            },
          },
        }, []);
        resolve(result);
      });
    });
  }

  /**
   * Opens a FB dialog to share a message.
   * Waits for promise to come back
   * If user cancels the share, it gets caught
   * @param {string} text
   * @param {string} base64image
   * @param {Object} payload
   * @param {string} intent
   * @param {string} messageType
   */
  async share(text, base64image, payload = null, intent, messageType) {
    this.showOverlay();
    try {
      await FBInstant.shareAsync({
        intent,
        image: base64image,
        text,
        data: payload,
      });
      this.hideOverlay();
      DDNA.socialTracker.incrementParam('numSocialShares', 1);
      DDNA.tracking.socialActionEvent(messageType, '', '', '');
      return true;
    } catch (e) {
      this.hideOverlay();
      if (e.code === 'USER_INPUT') {
        // console.log('User cancelled the share');
      } else {
        console.error(e);
      }
      return false;
    }
  }

  /**
   * share daily reward message
   * @param {Function} [callback]
   */
  shareDailyRewardGetMsg(callback) {
    new G.MsgDailyRewardGet(FBInstant.player.getPhoto(), (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      const txt = OMT.language.getText('I just got a Gift! Come and get yours!');
      this.share(txt, img, {
        sendParams: this._createMsgSendParams('DAILY_REWARD', 'SHARE'),
      }, SHARE_INTENT.SHARE, 'DAILY_REWARD')
        .then(callback);
    });
  }

  /**
   * share hard level complete message
   * @param {number} lvlIndex
   * @param {Function} [callback]
   */
  shareHardLevelCompleteMsg(lvlIndex, callback) {
    new G.MsgHardLevelComplete(FBInstant.player.getPhoto(), FBInstant.player.getName(), lvlIndex, (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      const txt = OMT.language.getText('I just beat this hard level! Can you?');
      this.share(txt, img, {
        sendParams: this._createMsgSendParams('HARD_LEVEL', 'SHARE'),
      }, SHARE_INTENT.SHARE, 'HARD_LEVEL')
        .then(callback);
    });
  }

  /**
   * share winning three times in a row message
   * @param {number} lvlIndex
   * @param {Function} [callback]
   */
  shareWonThreeTimes(lvlIndex, callback) {
    new G.MsgWonThreeTimes(FBInstant.player.getPhoto(), FBInstant.player.getName(), lvlIndex, (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      const txt = OMT.language.getText('I got a bunch of coins for winning 3 levels in a row! Try it out!');
      this.share(txt, img, {
        sendParams: this._createMsgSendParams('ACHIEVEMENT_GIFT', 'SHARE'),
      }, SHARE_INTENT.SHARE, 'ACHIEVEMENT_GIFT')
        .then(callback);
    });
  }

  /**
   * share mission complete message
   * @param {Function} [callback]
   */
  async shareMissionCompleteMsg(callback) {
    new G.MsgMissionsComplete(OMT.envData.settings.user.avatar, async (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      const txt = OMT.language.getText('I got unlimited lives! Finish all your Daily Missions!');
      await this.share(txt, img, {
        sendParams: this._createMsgSendParams('MISSION_COMPLETE', 'SHARE'),
      }, SHARE_INTENT.SHARE, 'MISSION_COMPLETE');
      if (callback) callback();
    });
  }

  /**
   * share treasure hunt complete message
   * @param {Function} [callback]
   */
  async shareTreasureHuntCompleteMsg(callback) {
    new G.MsgTreasureHuntComplete(OMT.envData.settings.user.avatar, async (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      const txt = OMT.language.getText('I beat Treasure Hunt and got lots of awesome boosters! Get yours too!');
      await this.share(txt, img, {
        sendParams: this._createMsgSendParams('TREASURE_HUNT', 'SHARE'),
      }, SHARE_INTENT.SHARE, 'TREASURE_HUNT');
      if (callback) callback();
    });
  }

  /**
   * share a fortune cookie message
   * @param {Function} callback
   * @param {string} imgID message image id ??
   * @param {string} msg message text
   * @param {string} category message category
   */
  shareFortuneCookieMsg(callback, imgID, msg, category) {
    new G.MsgFortuneCookie(imgID, msg, async (msgObj) => {
      const img = OMT.utils.msgToBase64(msgObj);
      msgObj.destroy();
      await this.share(msg, img, {
        fortuneCookie: {
          category,
          msg,
        },
        sendParams: this._createMsgSendParams('FORTUNE_COOKIE', 'SHARE'),
      }, SHARE_INTENT.SHARE, 'FORTUNE_COOKIE');
      if (callback) callback();
    });
  }

  /**
   * get the msg send param object for sending along with user messages
   * @param {string} msgId id / name of message
   * @returns {object}
   */
  _createMsgSendParams(msgName, msgType) {
    const stateObj = {
      senderId: OMT.envData.settings.user.userId,
      senderLevel: G.saveState.getLastPassedLevelNr(),
      senderDaysInGame: DDNA.tracking.enabled ? DDNA.tracking.getDataCapture().getPlayerCharacterizationParam('daysFromFirstLogin') : 0,
      senderSessionNumber: DDNA.tracking.enabled ? DDNA.tracking.getDataCapture().getPlayerCharacterizationParam('sessionNumber') : 0,
      senderCoins: G.saveState.getCoins(),
      senderIsPayer: G.saveState.getIAPCount() > 0 ? 1 : 0,
      timestamp: DDNA.utils.getTimestamp(),
      // epContextType : ?????
      // epPromoSourceId : crosspromo id,
      msgType,
      // epMsgContentID : ????
      msgLocale: OMT.envData.settings.env.locale,
      msgID: game.rnd.uuid().split('-').pop(),
      msgName,
    };
    return stateObj;
  }

  /**
   * show the overlay / interactive event blocker
   */
  showOverlay() {
    this.hideOverlay();
    this._overlay = new G.AdLoadOverlay();
  }

  /**
   * hide the overlay / interactive event blocker
   */
  hideOverlay() {
    if (!this._overlay) return;
    this._overlay.destroy();
    this._overlay = null;
  }

  /**
   * true if the overlay is active
   * @returns {boolean}
   */
  get isOverlayVisible() {
    return !(this._overlay === null || this._overlay === undefined);
  }
}
